// Provider
export { ForestProvider, type ForestProviderProps } from "./provider/ForestProvider";

// Theme
export { forestTheme, forestThemeOptions } from "./theme";

// Preset system
export {
  buildTheme,
  buildThemeOptions,
  getPreset,
  registerPreset,
  getAvailablePresets,
  forestExternalPreset,
  forestAgencyPreset,
  forestInternalPreset,
  forestAlkemyPlusPreset,
} from "./theme";
export type { DesignTokens, PresetVariantConfig, ThemePreset } from "./theme";

// Tokens (for consumers who need raw values)
export {
  base,
  error,
  warning,
  success,
  info,
  spacing,
  radius,
  shadows as tokenShadows,
  focusRings,
  fontFamily,
  fontFamilyMono,
  fontSize,
  lineHeight,
  fontWeight,
  display,
  container,
  widths,
  text,
  bg,
  fg,
  border,
  buttonColors,
  avatarColors,
  breadcrumbColors,
  iconColors,
  navColors,
  sliderColors,
  toggleColors,
  chartColors,
  alpha,
} from "./theme/tokens";

// Components — Inputs
export {
  Autocomplete,
  type AutocompleteProps,
  Button,
  type ButtonProps,
  ButtonGroup,
  type ButtonGroupProps,
  Checkbox,
  type CheckboxProps,
  Fab,
  type FabProps,
  IconButton,
  type IconButtonProps,
  RadioGroup,
  Radio,
  type RadioGroupProps,
  type RadioProps,
  Select,
  MenuItem,
  type SelectProps,
  type MenuItemProps,
  MultiSelect,
  type MultiSelectProps,
  type MultiSelectOption,
  Switch,
  type SwitchProps,
  TextField,
  type TextFieldProps,
  Search,
  type SearchProps,
  Input,
  type InputProps,
  ToggleButton,
  ToggleButtonGroup,
  type ToggleButtonProps,
  type ToggleButtonGroupProps,
  DatePicker,
  type DatePickerProps,
  Logo,
  type LogoProps,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  type FormControlProps,
  type FormControlLabelProps,
  type FormGroupProps,
  type FormHelperTextProps,
  type FormLabelProps,
  InputAdornment,
  type InputAdornmentProps,
  InputBase,
  type InputBaseProps,
  InputLabel,
  type InputLabelProps,
  OutlinedInput,
  FilledInput,
  type OutlinedInputProps,
  type FilledInputProps,
} from "./components";

// Components — Data display
export {
  Avatar,
  AvatarGroup,
  type AvatarProps,
  type AvatarGroupProps,
  Badge,
  type BadgeProps,
  Chip,
  type ChipProps,
  type ForestChipColor,
  type ForestChipProps,
  Divider,
  type DividerProps,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  type ImageListProps,
  type ImageListItemProps,
  type ImageListItemBarProps,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
  ListSubheader,
  ListItemSecondaryAction,
  type ListProps,
  type ListItemProps,
  type ListItemButtonProps,
  type ListItemIconProps,
  type ListItemTextProps,
  type ListItemAvatarProps,
  type ListSubheaderProps,
  type ListItemSecondaryActionProps,
  DataGrid,
  type DataGridProps,
  type GridColDef,
  type GridRowsProp,
  type GridRowParams,
  type GridCellParams,
  type GridRenderCellParams,
  type GridRowSelectionModel,
  type GridSortModel,
  type GridFilterModel,
  type GridPaginationModel,
  Rating,
  type RatingProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  TableSortLabel,
  type TableProps,
  type TableBodyProps,
  type TableCellProps,
  type TableContainerProps,
  type TableHeadProps,
  type TableRowProps,
  type TableFooterProps,
  type TablePaginationProps,
  type TableSortLabelProps,
  Tooltip,
  type TooltipProps,
  Typography,
  type TypographyProps,
} from "./components";

// Components — Surfaces
export {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
  type AccordionProps,
  type AccordionSummaryProps,
  type AccordionDetailsProps,
  type AccordionActionsProps,
  AppBar,
  Toolbar,
  AppBarNavItem,
  type AppBarProps,
  type ToolbarProps,
  type AppBarNavItemProps,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  CardMedia,
  type CardProps,
  type ForestCardColor,
  type CardContentProps,
  type CardHeaderProps,
  type CardActionsProps,
  type CardMediaProps,
  CardActionArea,
  type CardActionAreaProps,
  Drawer,
  SwipeableDrawer,
  type DrawerProps,
  type SwipeableDrawerProps,
  Paper,
  type PaperProps,
} from "./components";

// Components — Feedback
export {
  Alert,
  AlertTitle,
  type AlertProps,
  type AlertTitleProps,
  Backdrop,
  type BackdropProps,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  type DialogProps,
  type DialogTitleProps,
  type DialogContentProps,
  type DialogActionsProps,
  type DialogContentTextProps,
  LinearProgress,
  CircularProgress,
  type LinearProgressProps,
  type CircularProgressProps,
  Skeleton,
  type SkeletonProps,
  Modal,
  type ModalProps,
  Popover,
  type PopoverProps,
  Snackbar,
  type SnackbarProps,
  SnackbarContent,
  type SnackbarContentProps,
} from "./components";

// Components — Navigation
export {
  BottomNavigation,
  BottomNavigationAction,
  type BottomNavigationProps,
  type BottomNavigationActionProps,
  Breadcrumbs,
  type BreadcrumbsProps,
  Link,
  type LinkProps,
  Menu,
  MenuList,
  type MenuProps,
  type MenuListProps,
  MobileStepper,
  type MobileStepperProps,
  Pagination,
  type PaginationProps,
  PaginationItem,
  type PaginationItemProps,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  type SpeedDialProps,
  type SpeedDialActionProps,
  type SpeedDialIconProps,
  Stepper,
  Step,
  StepLabel,
  StepButton,
  StepConnector,
  StepContent,
  StepIcon,
  type StepperProps,
  type StepProps,
  type StepLabelProps,
  type StepButtonProps,
  type StepConnectorProps,
  type StepContentProps,
  type StepIconProps,
  Tabs,
  Tab,
  type TabsProps,
  type TabProps,
  TabScrollButton,
  type TabScrollButtonProps,
  SidebarNav,
  useSidebar,
  SidebarItem,
  type SidebarNavProps,
  type SidebarItemProps,
} from "./components";

// Components — Transitions
export {
  Collapse,
  type CollapseProps,
  Fade,
  type FadeProps,
  Grow,
  type GrowProps,
  Slide,
  type SlideProps,
  Zoom,
  type ZoomProps,
} from "./components";

// Components — Utilities
export {
  ButtonBase,
  type ButtonBaseProps,
  ClickAwayListener,
  type ClickAwayListenerProps,
  GlobalStyles,
  type GlobalStylesProps,
  NoSsr,
  Popper,
  type PopperProps,
  Portal,
  Slider,
  type SliderProps,
  SvgIcon,
  type SvgIconProps,
  TextareaAutosize,
} from "./components";

// Components — Layout
export {
  Box,
  type BoxProps,
  Stack,
  type StackProps,
  Grid,
  type GridProps,
  Container,
  type ContainerProps,
} from "./components";

// Components — Charts (Forest-themed)
export {
  LineChart,
  type LineChartProps,
  BarChart,
  type BarChartProps,
  PieChart,
  type PieChartProps,
  useChartColors,
} from "./components";

// Charts — Additional chart types
export { ScatterChart, SparkLineChart } from "./components";
export { Gauge, GaugeContainer, GaugeReferenceArc, GaugeValueArc, GaugeValueText } from "./components";

// Charts — Composition primitives
export {
  ChartContainer,
  ResponsiveChartContainer,
  ChartsSurface,
  ChartsClipPath,
  ChartsAxis,
  ChartsXAxis,
  ChartsYAxis,
  ChartsGrid,
  ChartsReferenceLine,
  ChartsLegend,
  DefaultChartsLegend,
  ChartsTooltip,
  ChartsAxisTooltipContent,
  ChartsItemTooltipContent,
  DefaultChartsAxisTooltipContent,
  DefaultChartsItemTooltipContent,
  ChartsTooltipCell,
  ChartsTooltipMark,
  ChartsTooltipPaper,
  ChartsTooltipRow,
  ChartsTooltipTable,
  ContinuousColorLegend,
  PiecewiseColorLegend,
  ChartsText,
  ChartsAxisHighlight,
  ChartsAxisHighlightPath,
  ChartsOnAxisClickHandler,
  ChartsVoronoiHandler,
  HighlightedProvider,
} from "./components";

// Charts — Plot layers
export {
  BarPlot,
  BarElement,
  BarElementPath,
  BarLabel,
  LinePlot,
  LineElement,
  LineElementPath,
  AreaPlot,
  AreaElement,
  AreaElementPath,
  LineHighlightPlot,
  LineHighlightElement,
  MarkPlot,
  MarkElement,
  AnimatedArea,
  AnimatedLine,
  PiePlot,
  PieArc,
  PieArcPlot,
  PieArcLabel,
  PieArcLabelPlot,
  ScatterPlot,
  Scatter,
} from "./components";

// Charts — Hooks
export {
  useDrawingArea,
  useChartId,
  useSvgRef,
  useXAxis,
  useXScale,
  useXColorScale,
  useYAxis,
  useYScale,
  useYColorScale,
  useZColorScale,
  useAxisTooltip,
  useItemTooltip,
  useMouseTracker,
  useHighlighted,
  useItemHighlighted,
  useGaugeState,
} from "./components";

// Charts — Palettes
export {
  blueberryTwilightPalette,
  blueberryTwilightPaletteDark,
  blueberryTwilightPaletteLight,
  cheerfulFiestaPalette,
  cheerfulFiestaPaletteDark,
  cheerfulFiestaPaletteLight,
  mangoFusionPalette,
  mangoFusionPaletteDark,
  mangoFusionPaletteLight,
} from "./components";

// Charts — Utilities
export { getSeriesToDisplay, getPieCoordinates } from "./components";

// Hooks
export { useMediaQuery } from "@mui/material";
export { useScrollTrigger } from "@mui/material";
export { useFormControl } from "@mui/material";
export { useTheme } from "@mui/material";
